import React, { useState, useEffect, useCallback } from "react";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import TextInput from "components/form/TextInput";
import FormButton from "components/form/FormButton";
import { getLanguageString as _ } from "misc/lang";
import { Paths } from "appConfig";
import {
  apiGetAccountList,
  apiGetHrAwards,
  apiCreateHrAward,
  apiUpdateHrAward,
  apiDeleteHrAward,
} from "net/requests";
import { Organisation } from "model/organisation";
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";
import css from "./awardsscreen.module.scss";

const AwardsScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const { admin, account, setAccount, globalAppMode, navigate } = props;
  const [organizations, setOrganizations] = useState<Organisation[]>([]);
  const [awards, setAwards] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [creationOpen, setCreationOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [awardToDelete, setAwardToDelete] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    coin_price: "",
    max_claims: "",
    is_active: true,
  });

  const mode = admin?.is_super_admin
    ? globalAppMode
    : account?.type?.toUpperCase();

  const loadOrganizations = useCallback(() => {
    if (!admin || !admin.is_super_admin || mode !== "HR") return;

    apiGetAccountList(admin, mode)
      .then((response) => {
        const orgs: Organisation[] = response.data;
        const topLevelOrgs = orgs.filter((org) => !org.parent_uuid);
        setOrganizations(topLevelOrgs);
      })
      .catch((error) => {
        console.error("Error loading organizations:", error);
      });
  }, [admin, mode]);

  const loadAwards = useCallback(() => {
    if (!admin || mode !== "HR" || !account?.uuid) {
      setAwards([]);
      return;
    }

    setLoading(true);
    setError(undefined);

    apiGetHrAwards(admin, account.uuid)
      .then((response) => {
        setAwards(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Error loading awards:", error);
        setError(_("awards", "error_loading_awards"));
        setAwards([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [admin, account?.uuid, mode]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    loadAwards();
  }, [loadAwards]);

  const hasSelectedAccount = !!account?.uuid;

  const isFormValid = () => {
    return (
      formData.name.trim().length > 0 &&
      formData.description.trim().length > 0 &&
      formData.coin_price.trim().length > 0 &&
      formData.max_claims.trim().length > 0 &&
      !isNaN(Number(formData.coin_price)) &&
      !isNaN(Number(formData.max_claims))
    );
  };

  const handleOpenCreate = () => setCreationOpen(true);
  const handleCloseCreate = () => setCreationOpen(false);

  const handleOpenDeleteDialog = (award: any) => {
    setAwardToDelete(award);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setAwardToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteAward = async () => {
    if (!admin || !awardToDelete) return;

    setActionLoading(true);
    setError(undefined);
    setSuccess(undefined);

    try {
      const awardId = awardToDelete.id || awardToDelete.uuid;
      await apiDeleteHrAward(admin, awardId);
      setSuccess(_("awards", "delete_success"));
      loadAwards();
    } catch (error) {
      console.error("Error deleting award:", error);
      setError(_("awards", "error_delete_award"));
    } finally {
      setActionLoading(false);
      handleCloseDeleteDialog();
    }
  };

  const handleToggleActive = async (award: any) => {
    if (!admin || !account?.uuid) return;

    setActionLoading(true);
    setError(undefined);
    setSuccess(undefined);

    try {
      const awardId = award.id || award.uuid;
      await apiUpdateHrAward(admin, awardId, {
        // account_id: account.uuid,
        name: award.name || award.title || "",
        description: award.description || "",
        coin_price: award.coin_price || 0,
        max_claims: award.max_claims || 0,
        is_active: !award.is_active,
      });
      setSuccess(_("awards", "toggle_success"));
      loadAwards();
    } catch (error) {
      console.error("Error toggling award active state:", error);
      setError(_("awards", "error_toggle_award"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateAward = async () => {
    if (!admin || !account?.uuid) return;

    setActionLoading(true);
    setError(undefined);
    setSuccess(undefined);

    try {
      await apiCreateHrAward(admin, {
        account_id: account.uuid,
        name: formData.name.trim(),
        description: formData.description.trim(),
        coin_price: Number(formData.coin_price),
        max_claims: Number(formData.max_claims),
        is_active: formData.is_active,
      });
      setSuccess(_("awards", "create_success"));
      setFormData({
        name: "",
        description: "",
        coin_price: "",
        max_claims: "",
        is_active: true,
      });
      setCreationOpen(false);
      loadAwards();
    } catch (error) {
      console.error("Error creating award:", error);
      setError(_("awards", "error_loading_awards"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewWinners = (award: any) => {
    const awardId = award.id || award.uuid;
    if (!awardId) return;
    navigate(Paths.AWARD_WINNERS.replace(":id", awardId));
  };

  return (
    <div className={css.container}>
      <PageHeader
        title={_("awards", "title")}
        subtitle={_("awards", "subtitle")}
      />

      {admin?.is_super_admin && mode === "HR" && (
        <div className={css.organizationSelectorOuter}>
          <div className={css.organizationSelector}>
            {organizations.map((org) => (
              <button
                key={org.uuid}
                className={`${css.orgButton} ${
                  account?.uuid === org.uuid ? css.selected : ""
                }`}
                onClick={() => setAccount(org as any)}
              >
                {org.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={css.contentBox}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        {hasSelectedAccount && mode === "HR" && (
          <Box className={css.actionButtons}>
            <FormButton
              label={_("awards", "btn_create_award")}
              onSubmit={handleOpenCreate}
              disabled={loading || actionLoading}
              inline={true}
              variant="contained"
              color="primary"
            />
          </Box>
        )}

        <Dialog open={creationOpen} onClose={handleCloseCreate} fullWidth>
          <DialogTitle>{_("awards", "create_title")}</DialogTitle>
          <DialogContent>
            <div className={css.formGrid}>
              <TextInput
                label={_("awards", "label_name")}
                initialValue={formData.name}
                updateValue={(value) =>
                  setFormData((prev) => ({ ...prev, name: value }))
                }
                labelOutside={true}
                placeholder={_("awards", "label_name")}
                isRequired={true}
              />
              <TextInput
                label={_("awards", "label_description")}
                initialValue={formData.description}
                updateValue={(value) =>
                  setFormData((prev) => ({ ...prev, description: value }))
                }
                labelOutside={true}
                placeholder={_("awards", "label_description")}
                isRequired={true}
              />
              <TextInput
                label={_("awards", "label_coin_price")}
                initialValue={formData.coin_price}
                updateValue={(value) =>
                  setFormData((prev) => ({ ...prev, coin_price: value }))
                }
                labelOutside={true}
                placeholder={_("awards", "label_coin_price")}
                type="number"
                isRequired={true}
              />
              <TextInput
                label={_("awards", "label_max_claims")}
                initialValue={formData.max_claims}
                updateValue={(value) =>
                  setFormData((prev) => ({ ...prev, max_claims: value }))
                }
                labelOutside={true}
                placeholder={_("awards", "label_max_claims")}
                type="number"
                isRequired={true}
              />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_active: event.target.checked,
                    }))
                  }
                />
              }
              label={_("awards", "label_is_active")}
            />
          </DialogContent>
          <DialogActions>
            <FormButton
              label={_("general", "cancel")}
              onSubmit={handleCloseCreate}
              inline={true}
              variant="outlined"
            />
            <FormButton
              label={_("awards", "btn_create_award")}
              onSubmit={handleCreateAward}
              disabled={!isFormValid() || actionLoading}
              inline={true}
              variant="contained"
              color="primary"
            />
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={deleteDialogOpen}
          title={_("awards", "delete_confirm_title")}
          message={_("awards", "delete_confirm_message")}
          itemName={awardToDelete?.name || awardToDelete?.title}
          confirmLabel={_("general", "btn_delete")}
          cancelLabel={_("general", "cancel")}
          onConfirm={handleDeleteAward}
          onCancel={handleCloseDeleteDialog}
        />

        {admin?.is_super_admin && mode === "HR" && !hasSelectedAccount && (
          <div className={css.emptyState}>
            <Typography className={css.title}>
              {_("awards", "select_organisation_title")}
            </Typography>
            <Typography className={css.subtitle}>
              {_("awards", "select_organisation_subtitle")}
            </Typography>
          </div>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && hasSelectedAccount && awards.length === 0 && (
          <div className={css.emptyState}>
            <Typography className={css.title}>
              {_("awards", "no_awards_title")}
            </Typography>
            <Typography className={css.subtitle}>
              {_("awards", "no_awards_subtitle")}
            </Typography>
          </div>
        )}

        {!loading && awards.length > 0 && (
          <div className={css.awardsList}>
            {awards.map((award, index) => (
              <div key={index} className={css.awardItem}>
                <div className={css.awardTitle}>
                  {award.title ||
                    award.name ||
                    `${_("awards", "award")} ${index + 1}`}
                </div>

                {award.description && (
                  <div className={css.awardDescription}>
                    {award.description}
                  </div>
                )}

                <div className={css.awardMeta}>
                  {award.created_at && (
                    <Typography variant="body2" className={css.awardMetaItem}>
                      <strong>{_("awards", "field_created")}:</strong>{" "}
                      {new Date(award.created_at).toLocaleDateString()}
                    </Typography>
                  )}
                  {award.is_active != null && (
                    <Typography variant="body2" className={css.awardMetaItem}>
                      <strong>{_("awards", "field_status")}:</strong>{" "}
                      {award.is_active
                        ? _("awards", "status_active")
                        : _("awards", "status_inactive")}
                    </Typography>
                  )}
                </div>

                <Box className={css.awardActions}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={award.is_active}
                        onChange={() => handleToggleActive(award)}
                        disabled={actionLoading}
                        color="primary"
                      />
                    }
                    label={_("awards", "label_is_active")}
                  />

                  <FormButton
                    label={_("awards", "btn_view_winners")}
                    onSubmit={() => handleViewWinners(award)}
                    inline={true}
                    variant="contained"
                    color="primary"
                    disabled={actionLoading}
                  />

                  <FormButton
                    label={_("general", "btn_delete")}
                    onSubmit={() => handleOpenDeleteDialog(award)}
                    inline={true}
                    variant="outlined"
                    color="error"
                    disabled={actionLoading}
                  />
                </Box>

                <div className={css.awardField}>
                  <strong>{_("awards", "field_price")}:</strong>{" "}
                  {award.coin_price}
                </div>
                <div className={css.awardField}>
                  <strong>{_("awards", "field_max_claims")}:</strong>{" "}
                  {award.max_claims}
                </div>
                {award.claimed_count != null && (
                  <div className={css.awardField}>
                    <strong>{_("awards", "field_claimed_count")}:</strong>{" "}
                    {award.claimed_count}
                  </div>
                )}
                {award.remaining_claims != null && (
                  <div className={css.awardField}>
                    <strong>{_("awards", "field_remaining_claims")}:</strong>{" "}
                    {award.remaining_claims}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AwardsScreen;
